import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router';
import { useCookieState } from 'ahooks';
import { Flow } from '@/constants/flow';
import { REGEXP_EMAIL } from '@/constants/regex';
import { useCoreRequest } from '@/hooks/use-core-request';
import { useBizStore } from '@/store/use-biz-store';
import { useConfigStore } from '@/store/use-config-store';
import { generateBid } from '@/utils/browser';
import { convertLocale } from '@/utils/locale';
import { useCore } from './use-core';
import { CUSTOMIZATION_MAP, DEFAULT_CUSTOMIZATION } from './use-custom/customization';

export function useWebInit() {
  const { i18n } = useTranslation();

  const [urlParams] = useSearchParams();
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();
  const [, setCtx] = useCookieState('ctx');
  const setConfig = useConfigStore((s) => s.setConfig);
  const ctx = urlParams.get('ctx') || '';

  const [isRedirecting, setIsRedirecting] = useState(false);
  const { lenovoId, setFlow } = useCore();
  const { setBizState } = useBizStore();

  const { loading } = useCoreRequest(() => lenovoId.webInit({ ctx }), {
    ready: ['/prelogin', '/register', '/resetpassword', '/recovery'].includes(pathname),
    onBefore: () => {
      setCtx(ctx);
    },
    onSuccess: ({ nextSteps, data }) => {
      const { config, callBack, userName, mfa, clientId, socials, entityId } = data;
      // ...

      // if userName belongs to email
      const urlUsername = urlParams.get('username');
      const urlAreaCode = urlParams.get('areacode')?.trim();

      let areaCode = data.areaCode || '+1';

      const { enabledAuthTypes } = CUSTOMIZATION_MAP.get(clientId) ?? DEFAULT_CUSTOMIZATION;

      let authType = enabledAuthTypes[0];
      let email = '';
      let phone = '';

      if (urlUsername && REGEXP_EMAIL.test(urlUsername) && enabledAuthTypes.includes('email')) {
        email = urlUsername;
        authType = 'email';
      }

      if (urlUsername && !REGEXP_EMAIL.test(urlUsername) && enabledAuthTypes.includes('phone')) {
        phone = urlUsername;
        authType = 'phone';
      }

      if (!data.areaCode && urlAreaCode && /^\+?\d{1,4}$/.test(urlAreaCode)) {
        const code = !urlAreaCode.startsWith('+') && Number(urlAreaCode) > 0 ? `+${urlAreaCode}` : urlAreaCode;
        const code2 = config.countryAreacodes.some((item) => item.countryCode === code) ? code : '+1';
        areaCode = code2;
      }

      const syncedBaseState = {
        userName: userName || urlUsername || '',
        authType,
        email,
        phone,
        googleVersion: config.gv || '3',
        areaCode,
        callBack,
      };

      generateBid(config.ip);
      setConfig({
        gsk2: config.gsk2,
        gsk3: config.gsk3,
        location: config.location,
        et: config.et || '1',
        ip: config.ip,
        areaCodeList: config.countryAreacodes.map((item) => ({ code: item.countryCode, prefix: item.prefix })),
        clientId,
        entityId,
        socials,
      });

      // sync locale...
      if (data.lang) {
        const convertedLocale = convertLocale(data.lang);
        i18n.changeLanguage(convertedLocale);
      }

      if (pathname === '/prelogin') {
        switch (nextSteps) {
          case 'LOGIN_STEP_INPUT_USERNAME':
            setBizState(syncedBaseState);
            setFlow(Flow.LOGIN_STEP_INPUT_USERNAME);
            break;

          case 'SSO_CHOOSE_ACCOUNT':
            setBizState(syncedBaseState);
            setFlow(Flow.LOGIN_STEP_CHOOSE_ACCOUNT);
            break;

          case 'ENFORCE_MFA_STEP_INPUT_CODE':
            setBizState({ ...syncedBaseState, mfa });
            setFlow(Flow.LOGIN_STEP_ENFORCED_MFA);
            break;

          case 'LOGIN_STEP_SUCCESS_CALLBACK':
            if (callBack) {
              setIsRedirecting(true);
              window.location.href = callBack;
            }
            break;
        }
      } else {
        setBizState(syncedBaseState);

        switch (pathname) {
          case '/register':
            setFlow(Flow.REGISTER_STEP_INPUT_USERINFO);
            break;
          case '/resetpassword':
            setFlow(Flow.RESET_PASSWORD_STEP_INPUT_USERNAME);
            break;
          case '/recovery':
            setFlow(Flow.RECOVERY_STEP_INPUT_USERNAME);
            break;

          default:
            break;
        }
      }
    },
  });

  return { loading: loading || isRedirecting };
}
